import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Button, Chip, IconButton, Typography } from "@material-ui/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import LockIcon from "@material-ui/icons/Lock";

import { scopeMeta, typeLabel } from "../../../utils/ruleDraft";
import { useRuleStyles } from "../styles";

export const usageText = (row) => {
  if (!row.ruleId) return "new rule";
  if (!row.usageCount || row.usageCount <= 1) return "only this set";
  return `shared by ${row.usageCount} sets`;
};

const RuleRow = ({
  row,
  index,
  count,
  type,
  disabled,
  onEdit,
  onEditEverywhere,
  onRemove,
  onMove,
}) => {
  const classes = useRuleStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.key, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const shared = Boolean(row.ruleId) && row.usageCount > 1;
  const meta = [
    typeLabel(type) || row.type,
    row.draft || row.rule ? scopeMeta(type, row.draft || row.rule) : null,
    usageText(row),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={[
        classes.ruleRow,
        isDragging ? classes.ruleRowDragging : "",
        row.ruleId ? "" : classes.ruleRowNew,
      ].join(" ")}
    >
      <IconButton
        className={`${classes.iconButton} ${classes.grip}`}
        aria-label={`Drag to reorder: ${row.text}`}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <DragHandleIcon />
      </IconButton>
      <Box minWidth={0}>
        <Typography className={classes.ruleText}>{row.text}</Typography>
        <Typography className={classes.ruleMeta}>
          {meta}
          {!row.ruleId && (
            <Chip
              size="small"
              label="new"
              className={classes.chipWarn}
              style={{ marginLeft: 6 }}
            />
          )}
        </Typography>
      </Box>
      <Box className={classes.actions}>
        <IconButton
          className={classes.iconButton}
          aria-label={`Move up: ${row.text}`}
          disabled={disabled || index === 0}
          onClick={() => onMove(index, index - 1)}
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          className={classes.iconButton}
          aria-label={`Move down: ${row.text}`}
          disabled={disabled || index >= count - 1}
          onClick={() => onMove(index, index + 1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          className={classes.iconButton}
          aria-label={
            shared
              ? `Replace in this set: ${row.text}`
              : `Edit rule: ${row.text}`
          }
          title={shared ? "Replace in this set" : "Edit rule"}
          disabled={disabled}
          onClick={() => onEdit(row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {shared && (
          <Button
            size="small"
            className={classes.touch}
            style={{ textTransform: "none" }}
            disabled={disabled}
            onClick={() => onEditEverywhere(row)}
            aria-label={`Edit everywhere: ${row.text}`}
          >
            Edit everywhere…
          </Button>
        )}
        <IconButton
          className={classes.iconButton}
          aria-label={`Remove from set: ${row.text}`}
          disabled={disabled}
          onClick={() => onRemove(row)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </li>
  );
};

RuleRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  type: PropTypes.object,
  disabled: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onEditEverywhere: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
};

RuleRow.defaultProps = { type: null, disabled: false };

/**
 * Ordered rules of a set. The pinned "number of dives" row sits on top and cannot move; the
 * rest reorder by drag (pointer or keyboard: space, arrows, space) or with the arrow buttons.
 */
const RuleList = ({
  rows,
  pinned,
  typesByKey,
  disabled,
  onReorder,
  onEdit,
  onEditEverywhere,
  onRemove,
}) => {
  const classes = useRuleStyles();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const ids = useMemo(() => rows.map((row) => row.key), [rows]);
  const textOf = (id) => rows.find((row) => row.key === id)?.text || "rule";
  const positionOf = (id) => ids.indexOf(id) + 1;

  const announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${textOf(active.id)}, position ${positionOf(active.id)} of ${
        rows.length
      }.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${textOf(active.id)} is over position ${positionOf(over.id)} of ${
            rows.length
          }.`
        : `${textOf(active.id)} is no longer over the list.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${textOf(active.id)} dropped at position ${positionOf(over.id)} of ${
            rows.length
          }.`
        : `${textOf(active.id)} dropped back at its position.`,
    onDragCancel: ({ active }) =>
      `Reordering of ${textOf(active.id)} cancelled.`,
  };

  const move = (from, to) => {
    if (from === to || to < 0 || to >= rows.length) return;
    onReorder(arrayMove(rows, from, to));
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(ids.indexOf(active.id), ids.indexOf(over.id));
  };

  return (
    <ul className={classes.ruleList} aria-label="Rules in this set">
      {pinned && (
        <li className={`${classes.ruleRow} ${classes.ruleRowPinned}`}>
          <Box display="flex" justifyContent="center" aria-hidden="true">
            <LockIcon style={{ color: "#98a2b3" }} />
          </Box>
          <Box minWidth={0}>
            <Typography className={classes.ruleText}>
              {pinned.text || "Number of dives"}
            </Typography>
            <Typography className={classes.ruleMeta}>
              {[
                "Number of dives · managed by the schema",
                pinned.usageCount > 1
                  ? `shared by ${pinned.usageCount} sets`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Box />
        </li>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable:
              "Press space to pick up a rule, use the arrow keys to move it, press space again to drop it, or escape to cancel.",
          },
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {rows.map((row, index) => (
            <RuleRow
              key={row.key}
              row={row}
              index={index}
              count={rows.length}
              type={typesByKey[row.type]}
              disabled={disabled}
              onEdit={onEdit}
              onEditEverywhere={onEditEverywhere}
              onRemove={onRemove}
              onMove={move}
            />
          ))}
        </SortableContext>
      </DndContext>
      {rows.length === 0 && (
        <li className={classes.muted} style={{ padding: "8px 0" }}>
          No rules beyond the number of dives yet. Add one below.
        </li>
      )}
    </ul>
  );
};

RuleList.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      ruleId: PropTypes.number,
      type: PropTypes.string,
      text: PropTypes.string,
      usageCount: PropTypes.number,
    }),
  ).isRequired,
  pinned: PropTypes.shape({
    text: PropTypes.string,
    usageCount: PropTypes.number,
  }),
  typesByKey: PropTypes.object,
  disabled: PropTypes.bool,
  onReorder: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onEditEverywhere: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

RuleList.defaultProps = { pinned: null, typesByKey: {}, disabled: false };

export default RuleList;
